/**
 * TeamShape — a team's mark (circle, rounded square, diamond or hexagon; see
 * `teamShape` in @warcarp/api-types) as one SVG. Used wherever a team is named with a mark: drawer headers, the
 * share bar, the dot strip legend, the end screen, the team-eliminated notice
 * and the lobby. Player dots and avatars draw their shape inside PlayerDisc.
 *
 * Decorative: callers name the team in text or an accessibility label.
 */
import React from 'react'
import { Platform, View, type StyleProp, type ViewStyle } from 'react-native'
import Svg, { Path } from 'react-native-svg'
import { teamShape, type TeamId, type TeamShapeName } from '@warcarp/api-types'
import { TEAM_SHAPE_MAX_EXTENT, teamShapePath } from '../../lib/teamShapes'

export function TeamShape({
  team,
  shape = team ? teamShape(team) : 'circle',
  size,
  fill,
  stroke,
  strokeWidth = 0,
  strokeDasharray,
  style,
  testID,
}: {
  /** The team whose shape to draw. */
  team?: TeamId
  /** An explicit shape, for callers without a team. */
  shape?: TeamShapeName
  /** Width and height of the mark. */
  size: number
  fill: string
  stroke?: string
  strokeWidth?: number
  /** A dashed outline (open and invited lobby seats). */
  strokeDasharray?: string
  style?: StyleProp<ViewStyle>
  testID?: string
}) {
  const c = size / 2
  // Every shape is drawn at one scale, so the optical-size diamond and hexagon
  // look as big as the circle; the box fits the farthest tip (the mockup's
  // padded viewBox) and the stroke, so nothing clips.
  const inner = ((size - strokeWidth) * 48) / TEAM_SHAPE_MAX_EXTENT
  return (
    <View
      testID={testID ?? (team ? `team-shape-${team}` : undefined)}
      pointerEvents="none"
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
      style={[{ width: size, height: size }, style]}
    >
      <Svg width={size} height={size} {...(Platform.OS === 'web' ? { 'aria-hidden': true } : { accessible: false })}>
        <Path d={teamShapePath(shape, c, c, inner)} fill={fill} stroke={stroke} strokeWidth={strokeWidth || undefined} strokeDasharray={strokeDasharray} />
      </Svg>
    </View>
  )
}
